import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKato } from '../../../contexts/kato.context';
import { useShared } from '../../../contexts/shared.context';
import KatoSelects from '../components/kato-selects.component';
import FooterComponent from '../components/footer.component';
import Field from '../../../components/field/field.component';
import { toast } from 'react-toastify';
import { getIncreaseVal, isEmpty, t } from '../../../helpers/utils';
import { GeneralInfoStatus } from '../../../types/kato.types';
import { updateGeneralInfoData } from '../../../requests/supervisor.request';
import classes from './styles.module.scss';
const fields = ['previousYearVal', 'currentYearVal', 'increaseVal'] as const;

const baseClass = 'infrastructure-tab';
const translationKey = 'admin.infrastructure';

const InfrastructureTab = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const { katoGeneralInfo, updatedGeneralInfo, currentLevel } = useKato();
  const { basicStats, status } = katoGeneralInfo || {};
  const { selectedInfoYear } = useShared();

  const [formData, setFormData] = useState({
    basicStats: basicStats || [],
    status: status || GeneralInfoStatus.IN_PROCESS,
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  const handleSave = () => {
    if (validateFields()) {
      const data = {
        basicStats: formData.basicStats,
        status: formData.status,
      };
      updateGeneralInfoData(katoGeneralInfo.kato, data, selectedInfoYear).then(
        () => {
          updatedGeneralInfo(selectedInfoYear);
          toast.success(t(`admin.success`, language));
        },
      );
    }
  };

  const validateFields = () => {
    const newErrors: Record<string, boolean> = {};

    const fields = formData.basicStats.filter(
      (stat) => stat.type === 'INFRASTRUCTURE',
    );

    const hasAtLeastOneValue = fields.some(
      (stat) => stat.previousYearVal != null || stat.currentYearVal != null,
    );

    if (hasAtLeastOneValue) {
      for (const stat of fields) {
        if (
          isEmpty(stat.previousYearVal) ||
          isEmpty(stat.currentYearVal) ||
          isEmpty(stat.additionalInfo?.previousYearVal) ||
          isEmpty(stat.additionalInfo?.currentYearVal)
        ) {
          newErrors[stat.subType] = true;
        }
      }
    }
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => !error);
  };

  useEffect(() => {
    setFormData({
      basicStats: basicStats || [],
      status: status || GeneralInfoStatus.IN_PROCESS,
    });
  }, [basicStats, katoGeneralInfo.kato, selectedInfoYear]);

  const renderFields = (lang: 'ru' | 'kz') => {
    return (
      <div className={classes['fields-container']}>
        {formData.basicStats
          .filter((item) => item.type === 'INFRASTRUCTURE')
          .map((item) => (
            <div
              className={classes['field-block']}
              key={`${item.type}-${item.subType}-${lang}`}
            >
              <div className={classes['field-block__title']}>
                {t(`${translationKey}.${item.subType}.title`, lang)}
              </div>
              <div className={classes['field-block__fields']}>
                <div className={classes['field-block__title']}>
                  {t(`${translationKey}.by-snp`, lang)}
                </div>
                {fields.map((field) => {
                  const isDisabled = field === 'increaseVal';
                  const value = isDisabled
                    ? getIncreaseVal(item, formData.basicStats)
                    : item[field];
                  return (
                    <Field
                      disabled={isDisabled}
                      key={`${item.type}-${item.subType}-${field}-${lang}-by-snp`}
                      value={value ?? ''}
                      type="number"
                      label={t(`${translationKey}.${field}`, lang, {
                        year:
                          field === 'currentYearVal'
                            ? selectedInfoYear
                            : selectedInfoYear - 1,
                        subtype: t(
                          `${translationKey}.${item.subType}.subtype`,
                          lang,
                        ),
                      })}
                      onChange={(value) => {
                        setFormData((prev) => ({
                          ...prev,
                          basicStats: prev.basicStats.map((stat) =>
                            stat.type === item.type &&
                            stat.subType === item.subType
                              ? {
                                  ...stat,
                                  [field]: value === '' ? null : +value,
                                }
                              : stat,
                          ),
                        }));
                      }}
                      required
                      error={errors[item.subType]}
                    />
                  );
                })}
                <div className={classes['field-block__title']}>
                  {t(`${translationKey}.by-population`, lang)}
                </div>
                {fields.map((field) => {
                  const isDisabled = field === 'increaseVal';
                  const value = isDisabled
                    ? getIncreaseVal(
                        {
                          ...item,
                          ...item.additionalInfo,
                        },
                        formData.basicStats,
                      )
                    : item.additionalInfo?.[field];
                  return (
                    <Field
                      disabled={isDisabled}
                      key={`${item.type}-${item.subType}-${field}-${lang}-by-population`}
                      value={value ?? ''}
                      type="number"
                      label={t(`${translationKey}.${field}`, lang, {
                        year:
                          field === 'currentYearVal'
                            ? selectedInfoYear
                            : selectedInfoYear - 1,
                        subtype: t(
                          `${translationKey}.${item.subType}.subtype`,
                          lang,
                        ),
                      })}
                      onChange={(value) => {
                        setFormData((prev) => ({
                          ...prev,
                          basicStats: prev.basicStats.map((stat) =>
                            stat.type === item.type &&
                            stat.subType === item.subType
                              ? {
                                  ...stat,
                                  additionalInfo: {
                                    ...stat.additionalInfo,
                                    [field]: value === '' ? '' : +value,
                                  },
                                }
                              : stat,
                          ),
                        }));
                      }}
                      required
                      error={errors[item.subType]}
                    />
                  );
                })}
              </div>
            </div>
          ))}
      </div>
    );
  };

  if (!basicStats || !katoGeneralInfo) return null;

  return (
    <div className={classes[baseClass]}>
      <div className={classes[`grid`]}>
        <KatoSelects lang={language as 'ru' | 'kz'} />
        <KatoSelects lang={language === 'ru' ? 'kz' : 'ru'} />
        {renderFields(language as 'ru' | 'kz')}
        {renderFields(language === 'ru' ? 'kz' : 'ru')}
      </div>

      <FooterComponent
        onSave={handleSave}
        status={formData.status}
        validateFields={validateFields}
      />
    </div>
  );
};

export default InfrastructureTab;
